<div>
    <div class="mb-4">
        <p-toolbar>
            <ng-template #start>
                <p-button
                    label="Exit wizard"
                    severity="danger"
                    [outlined]="true"
                    icon="pi pi-times"
                    (click)="exit()"
                ></p-button>
            </ng-template>
            <ng-template #center>
                <span class="font-bold text-2xl tracking-tight text-primary">
                    @if (realm(); as r) { {{ r.Domain }} } @else { New website }
                </span>
            </ng-template>
            <ng-template #end>
                <div class="flex items-center gap-3">
                    @if (stepError()) {
                    <span class="text-red-500 text-sm">{{ stepError() }}</span>
                    } @if (realm()) {
                    <p-tag
                        [value]="templateAssigned() ? 'Template assigned' : 'Offline — no template'"
                        [severity]="templateAssigned() ? 'success' : 'warn'"
                    ></p-tag>
                    }
                </div>
            </ng-template>
        </p-toolbar>
    </div>

    <div class="card">
        <p-stepper [value]="step()" [linear]="true" class="wizard-stepper">
            <p-step-list>
                <p-step [value]="STEP_BASICS">Basics</p-step>
                <p-step [value]="STEP_COMPANY">Company</p-step>
                <p-step [value]="STEP_TEMPLATE">Template</p-step>
                <p-step [value]="STEP_BRANDING">Branding</p-step>
                <p-step [value]="STEP_PAYMENT">Payment</p-step>
                <p-step [value]="STEP_FAQ">FAQ</p-step>
                <p-step [value]="STEP_GAMES">Games</p-step>
                <p-step [value]="STEP_DONE">Done</p-step>
            </p-step-list>

            <p-step-panels>
                <!-- ======================= Step 1: basics ======================= -->
                <p-step-panel [value]="STEP_BASICS">
                    <ng-template #content>
                        <div class="flex flex-col gap-4 pt-3 max-w-xl">
                            <p-fluid>
                                <div class="flex flex-col gap-4">
                                    <div class="flex flex-col gap-2">
                                        <label
                                            for="wizDomain"
                                            class="font-semibold"
                                            >Domain</label
                                        >
                                        <input
                                            pInputText
                                            id="wizDomain"
                                            [ngModel]="domain()"
                                            (ngModelChange)="setDomain($event)"
                                            placeholder="example.com"
                                            [disabled]="creating() || realm() !== null"
                                        />
                                        @if (domainError()) {
                                        <small class="text-red-500"
                                            >{{ domainError() }}</small
                                        >
                                        }
                                    </div>
                                    <div class="flex flex-col gap-2">
                                        <label
                                            for="wizDisplayName"
                                            class="font-semibold"
                                            >Display name</label
                                        >
                                        <input
                                            pInputText
                                            id="wizDisplayName"
                                            [ngModel]="displayName()"
                                            (ngModelChange)="displayName.set($event)"
                                            placeholder="SkillZone Fun"
                                            [disabled]="creating() || realm() !== null"
                                        />
                                        <small class="text-muted-color"
                                            >Used as the site name; the rest of
                                            Branding is configured later in this
                                            wizard.</small
                                        >
                                        @if (displayNameError()) {
                                        <small class="text-red-500"
                                            >{{ displayNameError() }}</small
                                        >
                                        }
                                    </div>
                                    <div class="flex flex-col gap-2">
                                        <label
                                            for="wizCategory"
                                            class="font-semibold"
                                            >Category</label
                                        >
                                        <p-select
                                            inputId="wizCategory"
                                            [options]="categoryOptions()"
                                            optionLabel="label"
                                            optionValue="value"
                                            [ngModel]="categoryId()"
                                            (ngModelChange)="categoryId.set($event)"
                                            placeholder="Select a category"
                                            appendTo="body"
                                            [loading]="categoryService.loading()"
                                            [disabled]="creating() || realm() !== null"
                                        ></p-select>
                                        <small class="text-muted-color"
                                            >Determines the template pool, the
                                            games catalog and the inherited
                                            currency / KYC / legal defaults.
                                            Cannot be changed after
                                            creation.</small
                                        >
                                        @if (categoryError()) {
                                        <small class="text-red-500"
                                            >{{ categoryError() }}</small
                                        >
                                        }
                                    </div>
                                    <div class="flex flex-col gap-2">
                                        <label
                                            for="wizSupportEmail"
                                            class="font-semibold"
                                            >Support email
                                            <span
                                                class="text-muted-color font-normal text-sm"
                                                >(optional)</span
                                            ></label
                                        >
                                        <input
                                            pInputText
                                            id="wizSupportEmail"
                                            type="email"
                                            [ngModel]="supportEmail()"
                                            (ngModelChange)="setSupportEmail($event)"
                                            placeholder="support@example.com"
                                            [disabled]="creating() || realm() !== null"
                                        />
                                        <small class="text-muted-color"
                                            >Shown to players as the support
                                            contact. Can be changed later in
                                            settings.</small
                                        >
                                        @if (supportEmailError()) {
                                        <small class="text-red-500"
                                            >{{ supportEmailError() }}</small
                                        >
                                        }
                                    </div>
                                </div>
                            </p-fluid>
                            <div class="flex justify-end pt-2">
                                @if (realm()) {
                                <p-button
                                    label="Next"
                                    icon="pi pi-arrow-right"
                                    iconPos="right"
                                    (onClick)="goTo(STEP_COMPANY)"
                                ></p-button>
                                } @else {
                                <p-button
                                    label="Next"
                                    icon="pi pi-arrow-right"
                                    iconPos="right"
                                    (onClick)="basicsNext()"
                                ></p-button>
                                }
                            </div>
                        </div>
                    </ng-template>
                </p-step-panel>

                <!-- ======================= Step 2: company ======================= -->
                <p-step-panel [value]="STEP_COMPANY">
                    <ng-template #content>
                        <div class="flex flex-col gap-4 pt-3 max-w-xl">
                            @if (realm()) {
                            <p-message
                                severity="info"
                                text="The website is already created — company can no longer be changed here. Use the General tab in full settings to reassign."
                            />
                            } @else {
                            <div class="flex items-center justify-between">
                                <span class="font-semibold">Company</span>
                                @if (companyOptions().length > 0) {
                                <div class="flex gap-1 text-xs">
                                    <button
                                        type="button"
                                        class="px-2 py-0.5 rounded border"
                                        [class.border-primary]="companyMode() === 'select'"
                                        [class.text-primary]="companyMode() === 'select'"
                                        [class.border-surface-300]="companyMode() !== 'select'"
                                        [class.text-muted-color]="companyMode() !== 'select'"
                                        [disabled]="creating()"
                                        (click)="setCompanyMode('select')"
                                    >
                                        Select existing
                                    </button>
                                    <button
                                        type="button"
                                        class="px-2 py-0.5 rounded border"
                                        [class.border-primary]="companyMode() === 'create'"
                                        [class.text-primary]="companyMode() === 'create'"
                                        [class.border-surface-300]="companyMode() !== 'create'"
                                        [class.text-muted-color]="companyMode() !== 'create'"
                                        [disabled]="creating()"
                                        (click)="setCompanyMode('create')"
                                    >
                                        + New
                                    </button>
                                </div>
                                }
                            </div>

                            <p-fluid>
                                @if (companyMode() === 'select') {
                                <div class="flex flex-col gap-2">
                                    <p-select
                                        [options]="companyOptions()"
                                        [ngModel]="selectedCompanyId()"
                                        (ngModelChange)="selectedCompanyId.set($event)"
                                        [filter]="true"
                                        [showClear]="true"
                                        placeholder="Select a company"
                                        appendTo="body"
                                        [disabled]="creating()"
                                    ></p-select>
                                    @if (companyError()) {
                                    <small class="text-red-500"
                                        >{{ companyError() }}</small
                                    >
                                    }
                                </div>
                                } @else {
                                <div
                                    class="border border-surface-200 dark:border-surface-700 rounded p-3 flex flex-col gap-3"
                                >
                                    <div class="flex flex-col gap-1">
                                        <label
                                            for="wizCompanyName"
                                            class="text-sm"
                                            >Name</label
                                        >
                                        <input
                                            pInputText
                                            id="wizCompanyName"
                                            [ngModel]="newCompanyName()"
                                            (ngModelChange)="newCompanyName.set($event)"
                                            placeholder="Acme Holdings Ltd."
                                            [disabled]="creating()"
                                        />
                                        @if (companyNameError()) {
                                        <small class="text-red-500"
                                            >{{ companyNameError() }}</small
                                        >
                                        }
                                    </div>
                                    <div class="flex flex-col gap-1">
                                        <label
                                            for="wizCompanyCountry"
                                            class="text-sm"
                                            >Country</label
                                        >
                                        <p-select
                                            inputId="wizCompanyCountry"
                                            [options]="countryOptions"
                                            optionLabel="label"
                                            optionValue="value"
                                            [ngModel]="newCompanyCountryCode()"
                                            (ngModelChange)="newCompanyCountryCode.set($event)"
                                            [filter]="true"
                                            [showClear]="true"
                                            placeholder="Select a country"
                                            appendTo="body"
                                            [disabled]="creating()"
                                        >
                                            <ng-template let-option #item>
                                                <div
                                                    class="flex items-center gap-2"
                                                >
                                                    <span
                                                        [class]="'flag flag-' + option.value.toLowerCase()"
                                                        style="
                                                            width: 18px;
                                                            height: 12px;
                                                        "
                                                    ></span>
                                                    <span
                                                        >{{ option.label
                                                        }}</span
                                                    >
                                                </div>
                                            </ng-template>
                                            <ng-template
                                                let-option
                                                #selectedItem
                                            >
                                                @if (option) {
                                                <div
                                                    class="flex items-center gap-2"
                                                >
                                                    <span
                                                        [class]="'flag flag-' + option.value.toLowerCase()"
                                                        style="
                                                            width: 18px;
                                                            height: 12px;
                                                        "
                                                    ></span>
                                                    <span
                                                        >{{ option.label
                                                        }}</span
                                                    >
                                                </div>
                                                }
                                            </ng-template>
                                        </p-select>
                                        @if (companyCountryError()) {
                                        <small class="text-red-500"
                                            >{{ companyCountryError() }}</small
                                        >
                                        }
                                    </div>
                                    <div class="flex flex-col gap-1">
                                        <label
                                            for="wizCompanyAddress"
                                            class="text-sm"
                                            >Address</label
                                        >
                                        <textarea
                                            pTextarea
                                            id="wizCompanyAddress"
                                            rows="2"
                                            [ngModel]="newCompanyAddress()"
                                            (ngModelChange)="newCompanyAddress.set($event)"
                                            [disabled]="creating()"
                                        ></textarea>
                                        @if (companyAddressError()) {
                                        <small class="text-red-500"
                                            >{{ companyAddressError() }}</small
                                        >
                                        }
                                    </div>
                                    <div class="flex flex-col gap-1">
                                        <label
                                            for="wizCompanyReg"
                                            class="text-sm"
                                            >Registration details</label
                                        >
                                        <textarea
                                            pTextarea
                                            id="wizCompanyReg"
                                            rows="2"
                                            [ngModel]="newCompanyRegDetails()"
                                            (ngModelChange)="newCompanyRegDetails.set($event)"
                                            placeholder="Reg. no. / VAT / Tax ID"
                                            [disabled]="creating()"
                                        ></textarea>
                                        @if (companyRegError()) {
                                        <small class="text-red-500"
                                            >{{ companyRegError() }}</small
                                        >
                                        }
                                    </div>
                                </div>
                                }
                            </p-fluid>
                            }

                            <div class="flex justify-between pt-2">
                                <p-button
                                    label="Back"
                                    severity="secondary"
                                    icon="pi pi-arrow-left"
                                    [disabled]="creating()"
                                    (onClick)="backToBasics()"
                                ></p-button>
                                @if (realm()) {
                                <p-button
                                    label="Next"
                                    icon="pi pi-arrow-right"
                                    iconPos="right"
                                    (onClick)="goTo(STEP_TEMPLATE)"
                                ></p-button>
                                } @else {
                                <p-button
                                    label="Create website"
                                    icon="pi pi-check"
                                    [loading]="creating()"
                                    (onClick)="createAndContinue()"
                                ></p-button>
                                }
                            </div>
                        </div>
                    </ng-template>
                </p-step-panel>

                <!-- ======================= Step 3: template ======================= -->
                <p-step-panel [value]="STEP_TEMPLATE">
                    <ng-template #content>
                        <div class="flex flex-col gap-4 pt-3">
                            @if (realm(); as r) { @if (!templateAssigned()) {
                            <p-message
                                severity="info"
                                text="The website is created but offline. Assigning a published template puts it online immediately — runtime branding, no build required."
                            />
                            }
                            <app-website-info-site-template
                                [realm]="r"
                                (realmUpdated)="onRealmUpdated($event)"
                            />
                            <div class="flex justify-between pt-2">
                                <span></span>
                                <p-button
                                    label="Next"
                                    icon="pi pi-arrow-right"
                                    iconPos="right"
                                    (onClick)="goTo(STEP_BRANDING)"
                                ></p-button>
                            </div>
                            }
                        </div>
                    </ng-template>
                </p-step-panel>

                <!-- ======================= Step 4: branding ======================= -->
                <p-step-panel [value]="STEP_BRANDING">
                    <ng-template #content>
                        <div class="flex flex-col gap-4 pt-3">
                            @if (realm(); as r) {
                            <app-website-info-branding
                                [realm]="r"
                                [(draft)]="brandingDraft"
                            />
                            <div class="flex justify-between pt-2">
                                <p-button
                                    label="Back"
                                    severity="secondary"
                                    icon="pi pi-arrow-left"
                                    [disabled]="saving()"
                                    (onClick)="goTo(STEP_TEMPLATE)"
                                ></p-button>
                                <p-button
                                    [label]="brandingDirty() ? 'Save & continue' : 'Next'"
                                    icon="pi pi-arrow-right"
                                    iconPos="right"
                                    [loading]="saving()"
                                    (onClick)="saveBrandingAndContinue()"
                                ></p-button>
                            </div>
                            }
                        </div>
                    </ng-template>
                </p-step-panel>

                <!-- ======================= Step 5: payment ======================= -->
                <p-step-panel [value]="STEP_PAYMENT">
                    <ng-template #content>
                        <div class="flex flex-col gap-4 pt-3">
                            @if (realm(); as r) {
                            <app-website-info-payment
                                [realm]="r"
                                [(fineraDraft)]="fineraDraft"
                                [(fineraWithdrawDraft)]="fineraWithdrawDraft"
                                [(paymentMethodDraft)]="paymentMethodDraft"
                            />
                            <div class="flex justify-between pt-2">
                                <p-button
                                    label="Back"
                                    severity="secondary"
                                    icon="pi pi-arrow-left"
                                    [disabled]="saving()"
                                    (onClick)="goTo(STEP_BRANDING)"
                                ></p-button>
                                <p-button
                                    [label]="paymentDirty() ? 'Save & continue' : 'Next'"
                                    icon="pi pi-arrow-right"
                                    iconPos="right"
                                    [loading]="saving()"
                                    (onClick)="savePaymentAndContinue()"
                                ></p-button>
                            </div>
                            }
                        </div>
                    </ng-template>
                </p-step-panel>

                <!-- ======================= Step 6: FAQ ======================= -->
                <p-step-panel [value]="STEP_FAQ">
                    <ng-template #content>
                        <div class="flex flex-col gap-4 pt-3">
                            @if (realm(); as r) {
                            <p-message
                                severity="secondary"
                                text="FAQ changes apply immediately. Use Import to copy entries from an existing website."
                            />
                            <app-website-info-faq [realmId]="r.RealmId" />
                            <div class="flex justify-between pt-2">
                                <p-button
                                    label="Back"
                                    severity="secondary"
                                    icon="pi pi-arrow-left"
                                    (onClick)="goTo(STEP_PAYMENT)"
                                ></p-button>
                                <p-button
                                    label="Next"
                                    icon="pi pi-arrow-right"
                                    iconPos="right"
                                    (onClick)="goTo(STEP_GAMES)"
                                ></p-button>
                            </div>
                            }
                        </div>
                    </ng-template>
                </p-step-panel>

                <!-- ======================= Step 7: games ======================= -->
                <p-step-panel [value]="STEP_GAMES">
                    <ng-template #content>
                        <div class="flex flex-col gap-4 pt-3">
                            @if (realm(); as r) {
                            <app-website-info-games
                                [realm]="r"
                                [providerStatus]="providerStatus()"
                            />
                            <div class="flex justify-between pt-2">
                                <p-button
                                    label="Back"
                                    severity="secondary"
                                    icon="pi pi-arrow-left"
                                    (onClick)="goTo(STEP_FAQ)"
                                ></p-button>
                                <p-button
                                    label="Finish"
                                    icon="pi pi-check"
                                    (onClick)="goTo(STEP_DONE)"
                                ></p-button>
                            </div>
                            }
                        </div>
                    </ng-template>
                </p-step-panel>

                <!-- ======================= Step 8: done ======================= -->
                <p-step-panel [value]="STEP_DONE">
                    <ng-template #content>
                        <div class="flex flex-col gap-4 pt-3 max-w-xl">
                            @if (realm(); as r) {
                            <div class="flex items-center gap-3">
                                <i
                                    class="pi pi-check-circle text-green-500"
                                    style="font-size: 2rem"
                                ></i>
                                <span class="text-xl font-semibold"
                                    >Website set up</span
                                >
                            </div>
                            <div
                                class="border border-surface-200 dark:border-surface-700 rounded p-4 flex flex-col gap-2 bg-surface-50 dark:bg-surface-800"
                            >
                                <div class="flex items-center gap-2">
                                    <a
                                        class="text-lg font-bold text-primary hover:underline inline-flex items-center gap-2"
                                        [href]="'https://' + r.Domain"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {{ r.Domain }}
                                        <i
                                            class="pi pi-external-link"
                                            style="font-size: 0.8rem"
                                        ></i>
                                    </a>
                                    <p-tag
                                        [value]="templateAssigned() ? 'Template assigned' : 'Offline — no template'"
                                        [severity]="templateAssigned() ? 'success' : 'warn'"
                                    ></p-tag>
                                </div>
                                <div
                                    class="text-sm text-muted-color flex flex-col gap-1"
                                >
                                    <span
                                        ><strong>Name:</strong> {{
                                        r.Branding.DisplayName }}</span
                                    >
                                    <span
                                        ><strong>Category:</strong> {{
                                        categoryName() }}</span
                                    >
                                    <span
                                        ><strong>Company:</strong> {{
                                        companyName() }}</span
                                    >
                                    <span
                                        ><strong>FAQ entries:</strong> {{
                                        faqCount() }}</span
                                    >
                                    <span
                                        ><strong>Games:</strong> {{ gamesCount()
                                        }}</span
                                    >
                                </div>
                            </div>
                            <p-message
                                severity="secondary"
                                text="Currency, KYC, Withdraw and Legal are inherited from the category. Override them in full settings if this site needs custom values."
                            />
                            <div class="flex justify-between pt-2">
                                <p-button
                                    label="Back to list"
                                    severity="secondary"
                                    icon="pi pi-list"
                                    (onClick)="backToList()"
                                ></p-button>
                                <p-button
                                    label="Open full settings"
                                    icon="pi pi-cog"
                                    (onClick)="openFullSettings()"
                                ></p-button>
                            </div>
                            }
                        </div>
                    </ng-template>
                </p-step-panel>
            </p-step-panels>
        </p-stepper>
    </div>
</div>

<p-dialog
    header="Leave wizard?"
    [visible]="leaveDialogVisible()"
    (visibleChange)="onLeaveCancel()"
    [style]="{ width: '28rem' }"
    [modal]="true"
    [closable]="true"
>
    <div class="flex items-center gap-3">
        <i
            class="pi pi-exclamation-triangle text-3xl"
            style="color: var(--p-orange-500)"
        ></i>
        @if (realm()) {
        <span
            >You have unsaved changes on the current step. Leave anyway? The
            website itself is already created and can be finished from its
            settings page.</span
        >
        } @else {
        <span
            >The website has not been created yet. Leave and discard the entered
            data?</span
        >
        }
    </div>
    <ng-template #footer>
        <div class="flex justify-end gap-2">
            <p-button
                label="Stay"
                severity="secondary"
                (onClick)="onLeaveCancel()"
            ></p-button>
            <p-button
                label="Leave"
                severity="danger"
                icon="pi pi-sign-out"
                (onClick)="onLeaveConfirm()"
            ></p-button>
        </div>
    </ng-template>
</p-dialog>

<p-toast />
